import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { StateResetAll } from 'ngxs-reset-plugin';
import { EMPTY, Observable, UnaryFunction, from, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import _ from 'lodash';
import * as Api from '../../../../generated/swagger';
import { SessionCookieService } from '../../../services/session-cookie.service';
import { BrickDefinitionState } from '../../dashboard/brick-wall/brick-definition/brick-definition.state';
import { AudioDevicesSettingsState } from '../../local-storage/local-storage-settings/audio-devices-settings.state';
import { DisconnectSignalR, ReconnectSignalR } from '../../server-communication/server-communication.actions';
import { SessionTypeEnum } from '../session-type-enum';
import { AuthenticationStateEnum } from './authentication-state-enum';
import {
   AuthenticateWithMicrosoftEntraIdToken,
   AuthenticateWithTeamsToken,
   AuthenticateWithWindowsAuthentication,
   AuthenticationResult,
   AuthenticationValidationResetAction,
   SignInAction,
   SignOutFromMicrosoftEntraId,
   SwitchSessionIdAction,
} from './authentication.actions';
import { MicrosoftEntraIdService } from 'src/app/services/microsoft-entra-id/microsoft-entra-id-service';
import { AuthenticationSettingsState } from '../../authentication-settings/authentication-settings.state';
import { AuthenticationSettingsSelectors } from '../../authentication-settings/authentication-settings.selectors';

export interface AuthenticationStateModel {
   authenticationState: AuthenticationStateEnum;
   wasMicrosoftEntraIdAuthenticationAttempt: boolean;
}

@State<AuthenticationStateModel>({
   name: 'authentication',
   defaults: {
      authenticationState: AuthenticationStateEnum.UNKNOWN,
      wasMicrosoftEntraIdAuthenticationAttempt: false,
   },
})
@Injectable()
export class AuthenticationState {
   constructor(
      private store: Store,
      private sessionsService: Api.SessionsService,
      private sessionCookieService: SessionCookieService,
      private microsoftEntraIdService: MicrosoftEntraIdService
   ) {}

   @Selector([AuthenticationState])
   public static getErrorIcon(state: AuthenticationStateModel): IconDefinition {
      return state.authenticationState.errorIcon;
   }

   @Selector([AuthenticationState])
   public static getErrorText(state: AuthenticationStateModel): string {
      return state.authenticationState.errorText;
   }

   @Selector([AuthenticationState])
   public static getShowError(state: AuthenticationStateModel): boolean {
      return state.authenticationState.showError;
   }

   @Selector([AuthenticationState])
   public static wasMicrosoftEntraIdAuthenticationAttempt(state: AuthenticationStateModel): boolean {
      return state.wasMicrosoftEntraIdAuthenticationAttempt;
   }

   @Action(AuthenticationResult)
   public AuthenticationFailed(ctx: StateContext<AuthenticationStateModel>, action: AuthenticationResult) {
      ctx.patchState({
         authenticationState: action.newState,
      });
   }

   @Action(AuthenticationValidationResetAction)
   public AuthenticationValidationReset(ctx: StateContext<AuthenticationStateModel>, action: AuthenticationValidationResetAction) {
      ctx.patchState({
         authenticationState: AuthenticationStateEnum.UNKNOWN,
         wasMicrosoftEntraIdAuthenticationAttempt: false,
      });
   }

   @Action(SignInAction)
   public signIn(ctx: StateContext<AuthenticationStateModel>, action: SignInAction) {
      return this.store.dispatch(new AuthenticationValidationResetAction()).pipe(
         switchMap(() =>
            this.sessionsService
               .signIn({
                  sessionType: SessionTypeEnum.WEB_AGENT.value,
                  username: action.username,
                  password: action.password,
               })
               .pipe(this.handleSignInResponse(ctx))
         )
      );
   }

   @Action(AuthenticateWithWindowsAuthentication)
   public authenticateWithWindowsAuthentication(ctx: StateContext<AuthenticationStateModel>, action: AuthenticateWithWindowsAuthentication) {
      return this.store.dispatch(new AuthenticationValidationResetAction()).pipe(
         switchMap(() =>
            this.sessionsService.windowsAuthenticate({
               sessionType: SessionTypeEnum.WEB_AGENT.value,
            })
         )
      );
   }

   @Action(AuthenticateWithTeamsToken)
   public authenticateWithTeamsToken(ctx: StateContext<AuthenticationStateModel>, action: AuthenticateWithTeamsToken) {
      if (ctx.getState().authenticationState.authenticated) return EMPTY;

      return this.store
         .dispatch(new AuthenticationValidationResetAction())
         .pipe(switchMap(() => this.sessionsService.teamsSSO({ teamsToken: action.teamsToken }).pipe(this.handleSignInResponse(ctx))));
   }

   @Action(SwitchSessionIdAction)
   public switchSessionId(ctx: StateContext<AuthenticationStateModel>, action: SwitchSessionIdAction) {
      console.log(`Switching Session ID to ${action.newSessionId}`);

      this.sessionCookieService.setNewSessionId(action.newSessionId);
      // Delete the manager cookie to prevent loading dashboard bricks in context of a wrong samwin user
      // after switching the WA user. Note: This may also affect a Manager instance running in another tab (when using the same domain).
      this.sessionCookieService.clearManagerCookie();
      ctx.dispatch(new DisconnectSignalR());
      // reset the whole state except AudioDeviceSettings (which are device-specific and saved in local storage), BrickDefinitions and AuthenticationSettings
      ctx.dispatch(new StateResetAll(AudioDevicesSettingsState, BrickDefinitionState, AuthenticationSettingsState));
      ctx.dispatch(new ReconnectSignalR());
   }

   @Action(AuthenticateWithMicrosoftEntraIdToken)
   public AuthenticateWithMicrosoftEntraIdToken(ctx: StateContext<AuthenticationStateModel>, action: AuthenticateWithMicrosoftEntraIdToken) {
      if (ctx.getState().authenticationState.authenticated) return EMPTY;

      return this.store.dispatch(new AuthenticationValidationResetAction()).pipe(
         tap(() => ctx.patchState({ wasMicrosoftEntraIdAuthenticationAttempt: true })),
         switchMap(() =>
            from(this.microsoftEntraIdService.authenticate(action.settings)).pipe(
               switchMap((authResult) => {
                  if (!_.isEmpty(authResult.accessToken)) {
                     return this.sessionsService.microsoftEntraIdSSO({ accessToken: authResult.accessToken }).pipe(this.handleSignInResponse(ctx));
                  } else {
                     return EMPTY;
                  }
               })
            )
         )
      );
   }

   @Action(SignOutFromMicrosoftEntraId)
   public async SignOutFromMicrosoftEntraId(ctx: StateContext<AuthenticationStateModel>, action: SignOutFromMicrosoftEntraId) {
      await this.microsoftEntraIdService.logout(action.settings);
   }

   private handleSignInResponse(ctx: StateContext<AuthenticationStateModel>): UnaryFunction<Observable<Api.SignInResponseData>, Observable<void>> {
      return pipe(
         // In case of an error the backend responds with an error code causing the request to fail
         // If successful no more action needed. Further proceedings is done by SignalR communication.
         switchMap(() => ctx.dispatch(new AuthenticationResult(AuthenticationStateEnum.AUTHENTICATED))),
         catchError(async (response: HttpErrorResponse) => {
            let authenticationResult: AuthenticationResult;

            const responseData = response.error as Api.SignInResponseData;
            if (responseData?.invalidPasswordOrUser) {
               authenticationResult = new AuthenticationResult(AuthenticationStateEnum.INVALID_CREDENTIALS);
            } else if (responseData?.userLocked) {
               authenticationResult = new AuthenticationResult(AuthenticationStateEnum.USER_LOCKED);
            } else if (responseData?.userNotAllowed) {
               authenticationResult = new AuthenticationResult(AuthenticationStateEnum.USER_NOT_ALLOWED);
            } else if (responseData?.invalidSession) {
               authenticationResult = new AuthenticationResult(AuthenticationStateEnum.AUTHENTICATION_FAILURE);
            } else {
               authenticationResult = new AuthenticationResult(AuthenticationStateEnum.AUTHENTICATION_FAILURE);
            }

            this.store.dispatch(authenticationResult);

            if (this.store.selectSnapshot(AuthenticationSettingsSelectors.useMicrosoftEntraIdAuthentication)) {
               this.microsoftEntraIdService.disableAutoMicrosoftEntraIdLogin();
            }
         })
      );
   }
}
